import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Mail, Send, CheckCircle, Eye, AlertTriangle } from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { SearchInput } from '@/components/shared/SearchInput';
import { StatCard } from '@/components/shared/StatCard';
import { StatusBadge } from '@/components/shared/StatusBadge';
import { Pagination } from '@/components/shared/Pagination';
import { Skeleton } from '@/components/ui/skeleton';
import { useGetEmails, useGetEmailStats } from '@/services/api/endpoints/EmailService';
import { useDebounce } from '@/hooks/useDebounce';
import { formatDate } from '@/lib/utils';
import type { EmailType, EmailStatus } from '@/types';

const EMAIL_TYPE_LABELS: Record<EmailType, string> = {
  welcome: 'Welcome',
  email_verification: 'Verifikacija',
  password_reset: 'Reset lozinke',
  password_changed: 'Lozinka promenjena',
  admin_notification: 'Admin obavestenje',
};

export function EmailsPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const [page, setPage] = useState(1);
  const [search, setSearch] = useState('');
  const [typeFilter, setTypeFilter] = useState<EmailType | ''>('');
  const [statusFilter, setStatusFilter] = useState<EmailStatus | ''>('');
  const debouncedSearch = useDebounce(search);

  const { data, isLoading } = useGetEmails(
    page, 20, debouncedSearch,
    typeFilter || undefined,
    statusFilter || undefined,
  );

  const { data: stats } = useGetEmailStats();

  return (
    <div className="space-y-6">
      <h1 className="text-2xl font-bold">{t('emails.title')}</h1>

      {/* Stats Cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard
          title={t('emails.totalSent')}
          value={stats?.sent ?? 0}
          icon={Send}
        />
        <StatCard
          title={t('emails.deliveryRate')}
          value={stats ? `${stats.deliveryRate}%` : '0%'}
          icon={CheckCircle}
        />
        <StatCard
          title={t('emails.openRate')}
          value={stats ? `${stats.openRate}%` : '0%'}
          icon={Eye}
        />
        <StatCard
          title={t('emails.bounceRate')}
          value={stats ? `${stats.bounceRate}%` : '0%'}
          icon={AlertTriangle}
        />
      </div>

      {/* Filters */}
      <div className="flex flex-wrap items-center gap-4">
        <div className="max-w-sm flex-1">
          <SearchInput value={search} onChange={setSearch} />
        </div>
        <select
          value={typeFilter}
          onChange={e => { setTypeFilter(e.target.value as EmailType | ''); setPage(1); }}
          className="flex h-10 rounded-md border border-input bg-background px-3 py-2 text-sm"
        >
          <option value="">{t('emails.allTypes')}</option>
          <option value="welcome">Welcome</option>
          <option value="email_verification">{t('emails.typeVerification')}</option>
          <option value="password_reset">{t('emails.typePasswordReset')}</option>
          <option value="password_changed">{t('emails.typePasswordChanged')}</option>
        </select>
        <select
          value={statusFilter}
          onChange={e => { setStatusFilter(e.target.value as EmailStatus | ''); setPage(1); }}
          className="flex h-10 rounded-md border border-input bg-background px-3 py-2 text-sm"
        >
          <option value="">{t('emails.allStatuses')}</option>
          <option value="queued">{t('emails.statusQueued')}</option>
          <option value="sent">{t('emails.statusSent')}</option>
          <option value="delivered">{t('emails.statusDelivered')}</option>
          <option value="opened">{t('emails.statusOpened')}</option>
          <option value="clicked">{t('emails.statusClicked')}</option>
          <option value="bounced">{t('emails.statusBounced')}</option>
          <option value="failed">{t('emails.statusFailed')}</option>
        </select>
      </div>

      {/* Email List */}
      <Card>
        <CardContent className="p-0">
          {isLoading ? (
            <div className="p-6 space-y-4">
              {Array.from({ length: 5 }).map((_, i) => (
                <Skeleton key={i} className="h-12" />
              ))}
            </div>
          ) : (
            <>
              {/* Desktop Table */}
              <div className="hidden md:block overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b bg-muted/50">
                      <th className="text-left p-4 text-sm font-medium text-muted-foreground">{t('emails.recipient')}</th>
                      <th className="text-left p-4 text-sm font-medium text-muted-foreground">{t('emails.type')}</th>
                      <th className="text-left p-4 text-sm font-medium text-muted-foreground">{t('emails.subject')}</th>
                      <th className="text-center p-4 text-sm font-medium text-muted-foreground">{t('common.status')}</th>
                      <th className="text-left p-4 text-sm font-medium text-muted-foreground">{t('emails.sentAt')}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data?.items.map((email) => (
                      <tr
                        key={email.id}
                        onClick={() => navigate(`/emails/${email.id}`)}
                        className="border-b hover:bg-muted/30 transition-colors cursor-pointer"
                      >
                        <td className="p-4 text-sm font-medium">{email.to}</td>
                        <td className="p-4 text-sm text-muted-foreground">
                          {EMAIL_TYPE_LABELS[email.type] || email.type}
                        </td>
                        <td className="p-4 text-sm max-w-xs truncate">{email.subject}</td>
                        <td className="p-4 text-center">
                          <StatusBadge status={email.status} />
                        </td>
                        <td className="p-4 text-sm text-muted-foreground">
                          {formatDate(email.sentAt || email.createdAt)}
                        </td>
                      </tr>
                    ))}
                    {data?.items.length === 0 && (
                      <tr>
                        <td colSpan={5} className="p-8 text-center text-muted-foreground">
                          {t('common.noResults')}
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>

              {/* Mobile Cards */}
              <div className="md:hidden divide-y">
                {data?.items.map((email) => (
                  <div
                    key={email.id}
                    onClick={() => navigate(`/emails/${email.id}`)}
                    className="p-4 space-y-2 cursor-pointer hover:bg-muted/30"
                  >
                    <div className="flex items-center justify-between">
                      <span className="text-sm font-medium">{email.to}</span>
                      <StatusBadge status={email.status} />
                    </div>
                    <div className="text-sm text-muted-foreground">
                      {EMAIL_TYPE_LABELS[email.type] || email.type}
                    </div>
                    <div className="text-xs text-muted-foreground truncate">{email.subject}</div>
                    <div className="text-xs text-muted-foreground">
                      {formatDate(email.sentAt || email.createdAt)}
                    </div>
                  </div>
                ))}
                {data?.items.length === 0 && (
                  <div className="p-8 text-center text-muted-foreground">
                    {t('common.noResults')}
                  </div>
                )}
              </div>
            </>
          )}
        </CardContent>
      </Card>

      {data?.pagination && (
        <Pagination page={page} totalPages={data.pagination.totalPages} onPageChange={setPage} />
      )}
    </div>
  );
}
